import { Fragment, PropsWithChildren, useEffect, useRef, useState } from 'react'
import mapboxgl from 'mapbox-gl'
import { Subject, throttleTime } from 'rxjs'
import { AsyncScheduler } from 'rxjs/internal/scheduler/AsyncScheduler'

import { InjectionTokens } from '@/controller/tokens'

import { Coordinates } from '@/domain/protocols/Coordinates'
import { MapPosition } from '@/domain/protocols/MapPosition'
import { RouteState, RouteStateProps } from '@/domain/states/RouteState'

import { MapClickEvent, MapClickEventFromMeasuringSegment, MapMouseMoveEvent } from '../../interfaces/MapEvents'
import { MapImperativeInterface, MapImperativeSubject } from '../../interfaces/MapImperativeInterface'
import { ScreenElementsState, ScreenElementsStateProps } from '../../states/ScreenElementsState'
import { getFitBoundaries } from '../../utils/map'
import { isIgnoredMapClickLayer } from './constants'
import { MapContext } from './MapContext'
import { userChartTransformRequest } from './userChartTransformRequest'

import 'mapbox-gl/dist/mapbox-gl.css'

import { useBehaviorSubject } from '@/presentation/hooks/useBehaviorSubject'
import { useGlobalState } from '@/presentation/hooks/useGlobalState'

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN

interface MapViewProps {
  defaultPosition: {
    center: Coordinates
    zoom: number
  }

  style?: string
}

const MouseMoveThrottler = new Subject<MapPosition>()

// Teto de espera pelo `idle` antes de uma captura. Serve só como escape: sem ele, uma tile que
// nunca chega deixaria a exportação pendurada para sempre.
const SCREENSHOT_IDLE_TIMEOUT = 3000

const waitForMapIdle = (map: mapboxgl.Map, timeout: number) =>
  new Promise<void>((resolve) => {
    let timer: ReturnType<typeof setTimeout>

    const handleIdle = () => {
      clearTimeout(timer)
      resolve()
    }

    timer = setTimeout(() => {
      map.off('idle', handleIdle)
      resolve()
    }, timeout)

    map.once('idle', handleIdle)
  })

function MapView(props: PropsWithChildren<MapViewProps>) {
  const { children, defaultPosition, style } = props

  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<mapboxgl.Map>(null)

  const [routeState] = useGlobalState<RouteState, RouteStateProps>(InjectionTokens.RouteState)
  const screenElementsState = useBehaviorSubject<ScreenElementsStateProps>(ScreenElementsState)

  const [isMapLoaded, setIsMapLoaded] = useState(false)
  const [isMapStyleLoaded, setIsMapStyleLoaded] = useState(false)
  // Bumped on every `style.load`. Mapbox can reload the style on its own (network
  // flap, token refresh, WebGL context loss), which clears all sources/layers.
  // Using this as the `key` of the layer subtree forces a remount so every layer
  // re-adds itself — otherwise the React bookkeeping desyncs from Mapbox and the
  // next add throws, tripping the "Falha nas camadas do mapa" boundary.
  const [styleEpoch, setStyleEpoch] = useState(0)

  const handleMapClick = (event: mapboxgl.MapMouseEvent) => {
    const position: MapPosition = {
      position: { x: event.point.x, y: event.point.y },
      coordinates: Coordinates.create({ latitude: event.lngLat.lat, longitude: event.lngLat.lng })
    }

    MapClickEventFromMeasuringSegment.next(position)

    const ignoredLayers: string[] = []

    mapRef.current.getStyle().layers.forEach((layer) => {
      if (isIgnoredMapClickLayer(layer.id)) {
        ignoredLayers.push(layer.id)
      }
    })

    const features = mapRef.current.queryRenderedFeatures(event.point, { layers: ignoredLayers })
    if (features.length > 0) return

    MapClickEvent.next(position)
  }

  const handleMapMouseMove = (event: mapboxgl.MapMouseEvent) => {
    const position = {
      position: { x: event.point.x, y: event.point.y },
      coordinates: Coordinates.create({ latitude: event.lngLat.lat, longitude: event.lngLat.lng })
    }

    MouseMoveThrottler.next(position)
  }

  const handleMapLoaded = () => {
    setIsMapLoaded(true)
  }

  const handleMapStyleLoaded = () => {
    setIsMapStyleLoaded(true)
    setStyleEpoch((epoch) => epoch + 1)
  }

  useEffect(() => {
    if (mapRef.current) return

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current as HTMLDivElement,
      center: [defaultPosition.center.longitude, defaultPosition.center.latitude],
      zoom: defaultPosition.zoom,
      style: style ?? 'mapbox://styles/mapbox/streets-v11',
      transformRequest: userChartTransformRequest,
      attributionControl: false,
      dragRotate: false,
      touchPitch: false,
      pitchWithRotate: false,
      touchZoomRotate: true,
      dragPan: true,
      maxPitch: 0,
      logoPosition: 'top-right',
      doubleClickZoom: false,
      projection: { name: 'mercator' }
    })

    mapRef.current.touchZoomRotate.disableRotation()
    mapRef.current.on('click', handleMapClick)
    mapRef.current.on('mousemove', handleMapMouseMove)
    mapRef.current.on('load', handleMapLoaded)
    mapRef.current.on('style.load', handleMapStyleLoaded)

    // Start listening to map events
    const eventsSubscription = MapImperativeSubject.subscribe((event) => {
      switch (event.type) {
        case 'flyToBounds':
          mapRef.current?.fitBounds(event.bounds, {
            padding: {
              top: event.padding?.[0] ?? 0,
              right: event.padding?.[1] ?? 0,
              bottom: event.padding?.[2] ?? 0,
              left: event.padding?.[3] ?? 0
            },
            duration: event.duration
          })
          break
        case 'flyToCoordinates':
          mapRef.current?.easeTo({
            center: [event.coordinates.longitude, event.coordinates.latitude],
            zoom: event.zoom ?? mapRef.current?.getZoom(),
            duration: event.duration,
            padding: {
              top: event.padding?.[0] ?? 0,
              right: event.padding?.[1] ?? 0,
              bottom: event.padding?.[2] ?? 0,
              left: event.padding?.[3] ?? 0
            }
          })
          break
        case 'zoomIn':
          mapRef.current?.zoomIn()
          break
        case 'zoomOut':
          mapRef.current?.zoomOut()
          break
        case 'takeScreenshot': {
          if (!mapRef.current) return

          const map = mapRef.current

          const captureCurrentFrame = (onCaptured: (screenshot: string) => void) => {
            map.once('render', () => onCaptured(map.getCanvas().toDataURL()))

            /* trigger render */
            map.setBearing(map.getBearing())
          }

          if (!event.bounds) {
            captureCurrentFrame(event.callback)
            break
          }

          // Guarda a câmera para devolvê-la depois: enquadrar a rota é uma necessidade da captura,
          // não algo que deva mexer no mapa que o usuário está vendo.
          const previousCamera = {
            center: map.getCenter(),
            zoom: map.getZoom(),
            bearing: map.getBearing(),
            padding: map.getPadding()
          }

          map.fitBounds(event.bounds, {
            padding: {
              top: event.padding?.[0] ?? 0,
              right: event.padding?.[1] ?? 0,
              bottom: event.padding?.[2] ?? 0,
              left: event.padding?.[3] ?? 0
            },
            duration: 0
          })

          // `idle` só dispara com a câmera parada E os tiles do novo enquadramento renderizados —
          // é o que garante que a foto não saia de um frame anterior. O timeout evita travar a
          // exportação caso ele não venha (rede lenta, tile indisponível).
          waitForMapIdle(map, SCREENSHOT_IDLE_TIMEOUT).then(() => {
            captureCurrentFrame((screenshot) => {
              map.jumpTo(previousCamera)
              event.callback(screenshot)
            })
          })
          break
        }
      }
    })

    const mouseMoveThrottlerSubscribe = MouseMoveThrottler.pipe(
      throttleTime(41.67, AsyncScheduler as any, { leading: true, trailing: true })
    ).subscribe((position) => {
      MapMouseMoveEvent.next(position)
    })

    return () => {
      eventsSubscription.unsubscribe()
      mapRef.current?.off('click', handleMapClick)
      mapRef.current?.off('load', handleMapLoaded)
      mapRef.current?.off('style.load', handleMapStyleLoaded)
      mouseMoveThrottlerSubscribe.unsubscribe()
      mapRef.current?.remove()
    }
  }, [])

  useEffect(() => {
    if (!mapRef.current) return
    setIsMapStyleLoaded(false)
    mapRef.current.setStyle(style ?? 'mapbox://styles/mapbox/streets-v11')
  }, [style])

  useEffect(() => {
    if (!mapRef.current) return
    const boundaries = getFitBoundaries(routeState.activeRoute)
    const padding: [number, number, number, number] = [
      40 + screenElementsState.boundsDefaultPadding.top,
      75 + screenElementsState.boundsDefaultPadding.right,
      40 + screenElementsState.boundsDefaultPadding.bottom,
      75 + screenElementsState.boundsDefaultPadding.left
    ]
    if (isMapLoaded && isMapStyleLoaded) {
      setTimeout(() => {
        if (routeState.activeRoute?.waypoints.length === 1) {
          MapImperativeInterface.flyToCoordinates(routeState.activeRoute.waypoints[0].coordinates, 8, 2000, padding)
          return
        }
        MapImperativeInterface.flyToBounds(boundaries, routeState.activeRoute ? padding : null, 2000)
      }, 1000)
    }
  }, [isMapLoaded])

  return (
    <>
      <div
        ref={mapContainerRef}
        id="map-container"
        style={{
          height: '100%',
          width: '100%',
          position: 'absolute'
        }}
      />
      {isMapLoaded && isMapStyleLoaded && (
        <MapContext.Provider value={{ map: mapRef.current }}>
          {/* Keyed by styleEpoch so a spontaneous Mapbox style reload remounts the
              whole layer subtree and re-adds every source/layer. The provider stays
              outside the Fragment to keep the map instance identity stable. */}
          <Fragment key={styleEpoch}>{children}</Fragment>
        </MapContext.Provider>
      )}
    </>
  )
}

export { MapView }
